import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../hooks/useAuth';
import { getPortalData, submitRecord } from '../lib/api';
import { statusLabels } from '../config/brand';
import { Notice } from '../components/Shared';

const { FiExternalLink, FiCreditCard, FiCalendar, FiFileText, FiBell, FiHelpCircle } = FiIcons;

export default function Account() {
  const { session, profile, loading, signOut } = useAuth();
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (session?.user) getPortalData(session.user.id).then(setData).catch((error) => setMessage(error.message));
  }, [session]);

  if (loading) return <div className="page-loader">Loading…</div>;
  if (!session) return <Navigate to="/login" replace />;

  const subscription = data?.subscription;
  const accessUrl = subscription?.licences?.activation_url;

  const submitTicket = async (event) => {
    event.preventDefault();
    try {
      await submitRecord('support_requests', { ...Object.fromEntries(new FormData(event.currentTarget)), user_id: session.user.id });
      event.currentTarget.reset();
      setMessage('Your support request has been submitted.');
    } catch (error) {
      setMessage(error.message);
    }
  };

  return (
    <section className="dashboard-page">
      <div className="dashboard-heading">
        <div><span className="eyebrow">Customer portal</span><h1>Welcome, {profile?.full_name || 'there'}</h1></div>
        <button className="button secondary small" onClick={signOut}>Sign out</button>
      </div>
      {message && <Notice type="success">{message}</Notice>}
      <div className="dashboard-grid">
        <article className="card subscription-card">
          <div><span className="status">{statusLabels[subscription?.status] || 'No active subscription'}</span><h2>{subscription?.plans?.name || 'Choose your coaching plan'}</h2></div>
          {accessUrl ? <a className="button full" href={accessUrl} target="_blank" rel="noreferrer">Open My AI Coach <SafeIcon icon={FiExternalLink} /></a> : <Notice>Your coaching access is being prepared. You will receive an email as soon as your account has been activated.</Notice>}
        </article>
        <Metric icon={FiCalendar} label="Next renewal" value={subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : '—'} />
        <Metric icon={FiCreditCard} label="Billing status" value={statusLabels[subscription?.status] || 'No subscription'} />
        <Metric icon={FiFileText} label="Invoices" value={`${data?.payments?.length || 0} payment records`} />
      </div>
      <div className="portal-columns">
        <section className="card">
          <h2><SafeIcon icon={FiBell} /> Notifications</h2>
          <div className="list">{data?.notifications?.length ? data.notifications.map((item) => <div key={item.id}><strong>{item.title}</strong><p>{item.message}</p></div>) : <p>No new notifications.</p>}</div>
        </section>
        <section className="card">
          <h2><SafeIcon icon={FiHelpCircle} /> Customer support</h2>
          <form onSubmit={submitTicket}>
            <label>Subject<input name="subject" required /></label>
            <label>Category<select name="category">{['Account', 'Billing', 'Coaching Access', 'Technical Problem', 'Subscription', 'Other'].map((item) => <option key={item}>{item}</option>)}</select></label>
            <label>Priority<select name="priority"><option>Normal</option><option>High</option><option>Urgent</option></select></label>
            <label>Message<textarea name="message" rows="4" required /></label>
            <button className="button">Submit request</button>
          </form>
        </section>
      </div>
    </section>
  );
}

function Metric({ icon, label, value }) {
  return <article className="card metric"><SafeIcon icon={icon} /><span>{label}</span><strong>{value}</strong></article>;
}